import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Lottie from 'lottie-react';
import { motion } from 'framer-motion';
import { trackEvent } from '../utils/analytics';
import TeaserCarousel from './TeaserCarousel';

import viewSolveAnimation from '../animations/view_solve_qb.json';
import generatePaperAnimation from '../animations/generate_question_paper.json';

import './ModeSelectionPage.css';

function ModeSelectionPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const category = params.get("category") || "school";
  const qParam = params.get("q") || "";

  useEffect(() => {
    if (qParam) {
      const targetPath = `/select?category=${category}&q=${encodeURIComponent(qParam)}`;
      navigate(targetPath);
    }
  }, [qParam, category, navigate]);

  if (qParam) return null;

  const categoryMeta = category === "school"
    ? {
        badge: 'School Mode',
        title: 'Solve Previous Year Papers Or Generate Sample Papers',
        subtitle: 'Practice board-based previous year questions or generate sample question papers using syllabus, grade, subject, chapter, term, and difficulty.',
        accentClass: 'mode-page-school',
        glowClass: 'mode-orb-school',
      }
    : {
        badge: 'College Mode',
        title: 'Practice University Papers Or Build Custom Semester Papers',
        subtitle: 'Solve previous year university question papers or generate sample papers using university, program, stream, semester, subject, chapter, and difficulty.',
        accentClass: 'mode-page-college',
        glowClass: 'mode-orb-college',
      };

  return (
    <div className={`mode-page ${categoryMeta.accentClass}`}>
      <div className={`mode-orb mode-orb-left ${categoryMeta.glowClass}`} />
      <div className="mode-orb mode-orb-right" />

      <section className="mode-shell">
        <motion.div
          className="mode-hero"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
        >
          <span className="mode-badge">{categoryMeta.badge}</span>
          <h1 className="mode-title">{categoryMeta.title}</h1>
          <p className="mode-subtitle">{categoryMeta.subtitle}</p>
        </motion.div>

        <motion.div
          className="mode-grid"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: {
              transition: {
                staggerChildren: 0.1,
              },
            },
          }}
        >
          <motion.div
            className="mode-card mode-card-practice"
            onClick={() => {
              trackEvent("solve_clicked", { category, source: "mode_selection" });
              navigate(`/select?category=${category}`);
            }}
            whileHover={{ y: -8, scale: 1.02 }}
            whileTap={{ scale: 0.985 }}
            variants={{
              hidden: { opacity: 0, y: 30 },
              visible: { opacity: 1, y: 0 },
            }}
          >
            <div className="mode-card-chip">Focused Prep</div>
            <Lottie animationData={viewSolveAnimation} loop className="mode-lottie" />
            <h2>Practice</h2>
            <p>Drill into previous year question papers with a guided selection flow built for steady progress.</p>
            <div className="mode-card-footer">
              <span>Open Practice</span>
            </div>
          </motion.div>

          <motion.div
            className="mode-card mode-card-generate"
            onClick={() => {
              trackEvent("generate_clicked", { category, source: "mode_selection" });
              navigate(`/generate?category=${category}`);
            }}
            whileHover={{ y: -8, scale: 1.02 }}
            whileTap={{ scale: 0.985 }}
            variants={{
              hidden: { opacity: 0, y: 30 },
              visible: { opacity: 1, y: 0 },
            }}
          >
            <div className="mode-card-chip">Smart Assembly</div>
            <Lottie animationData={generatePaperAnimation} loop className="mode-lottie" />
            <h2>Generate</h2>
            <p>Create a custom paper in seconds using your selected board, stream, semester, subject, and difficulty.</p>
            <div className="mode-card-footer">
              <span>Generate Paper</span>
            </div>
          </motion.div>
        </motion.div>

        <TeaserCarousel 
          category={category} 
          style={{ marginTop: '40px', paddingBottom: '20px' }}
        />
      </section>
    </div>
  );
}

export default ModeSelectionPage;
